/**
 * What happened when the user asked us to send them a verification mail (ui#1458, ui#1477).
 *
 * Plain TypeScript — no React and no Firebase SDK — so the rule that turns a transport error into
 * something a person can act on is testable on its own and cannot drift into a component.
 */
export type EmailVerificationSendOutcome = "sent" | "rateLimited" | "network" | "signedOut" | "failed";

/** Every outcome that is NOT success. A failure classifier must not be able to report one. */
export type EmailVerificationSendFailure = Exclude<EmailVerificationSendOutcome, "sent">;

/**
 * There is no signed-in Firebase user to send a verification mail to. Raised by the auth
 * implementation rather than inferred from an error, because there is nothing to reject with when we
 * never call anything.
 */
export class NoSignedInUserError extends Error {
	constructor() {
		super("No signed-in Firebase user to send a verification email to");
		this.name = "NoSignedInUserError";
	}
}

/**
 * The api's answer for "this address is already confirmed" (api-service#863).
 *
 * Not a failure: nothing is wrong and there is nothing for the user to fix, so it settles the callout
 * as `alreadyVerified` — which sends nothing and claims no waiting inbox — rather than reaching the
 * classifier below at all.
 */
export const ALREADY_VERIFIED_STATUS = 409;

/**
 * Statuses meaning "this session is no longer usable". The global axios interceptor already forced a
 * token refresh and retried once before a 401 reaches us, so one that survives that is a session
 * genuinely gone rather than a token that expired in flight.
 *
 * The copy this maps to states the EFFECT ("you are not signed in any more"), never the cause — a
 * confident wrong cause is the defect ui#1458 exists to stop repeating.
 */
const STALE_SESSION_STATUSES: ReadonlySet<number> = new Set([
	401,
	403,
	/**
	 * The api has no address on this identity to prove (422, api-service#863). Grouped here rather than
	 * left to fall through to `failed`, whose copy is "please try again" — an instruction that can never
	 * succeed, since retrying cannot give an identity an address. "Sign in again" is the move that can:
	 * the address arrives with the session, so a session carrying one is what changes the answer.
	 */
	422,
]);

/**
 * The status an answered HTTP rejection carries, or null when the request was never answered.
 *
 * Duck-typed rather than `axios.isAxiosError`, exactly as `login/passwordResetDelivery` does it: the
 * guard is a runtime import of axios, and this module is deliberately dependency-free so it can be
 * tested with plain object literals.
 */
function answeredStatus(error: unknown): number | null {
	const status = (error as {response?: {status?: number}} | null | undefined)?.response?.status;
	return typeof status === "number" ? status : null;
}

/** Whether this is a request that reached no server, as opposed to something not shaped like one. */
function neverAnswered(error: unknown): boolean {
	if (error === null || typeof error !== "object") {
		return false;
	}
	// The two codes that mean "this request reached no server". Narrower than `isAxiosError === true`,
	// which also covers a cancelled request and a malformed config — neither of which is the user's
	// connection, and both of which would send them to check it for nothing.
	const {code} = error as {code?: unknown};
	return code === "ERR_NETWORK" || code === "ECONNABORTED";
}

/**
 * Whether the api refused because the address is already confirmed, which is not a failure.
 *
 * Callers settle this as `alreadyVerified`; passing it to
 * {@link classifyEmailVerificationSendFailure} instead would show a "couldn't send" line for a
 * request that was answered exactly as it should have been.
 */
export function isAlreadyVerified(error: unknown): boolean {
	return answeredStatus(error) === ALREADY_VERIFIED_STATUS;
}

/**
 * The outcome to show for a failed send.
 *
 * `failed` is the fallback for an unrecognised error on purpose: guessing a specific cause would put
 * a confident, wrong instruction in front of the user, which is the defect ui#1458 exists to stop
 * repeating. In particular a 502 — our mail path is down — is deliberately `failed` and not
 * `network`: the user's own connection is fine and telling them to check it would be that same wrong
 * instruction.
 */
export function classifyEmailVerificationSendFailure(error: unknown): EmailVerificationSendFailure {
	if (error instanceof NoSignedInUserError) {
		return "signedOut";
	}
	const status = answeredStatus(error);
	if (status === null) {
		return neverAnswered(error) ? "network" : "failed";
	}
	if (status === 429) {
		return "rateLimited";
	}
	return STALE_SESSION_STATUSES.has(status) ? "signedOut" : "failed";
}
